import { Rock5aAudioReceiver } from '@/utils/rock5a_audio.js'
import { getRock5aDeviceSession } from './rock5aDeviceSession.js'
import { connectionContextToDevice } from './rock5aConnectionContext.js'

const API_GATEWAY_URL = (process.env.VUE_APP_API_GATEWAY_URL || '').replace(/\/$/, '')
const API_GATEWAY_TOKEN = process.env.VUE_APP_API_GATEWAY_TOKEN || ''
const ASR_SESSION_PATH = '/v1/audio/transcriptions/session'
const TTS_API_PATH = '/v1/audio/speech'
const TTS_MODEL = 'FunAudioLLM/CosyVoice2-0.5B'
const TTS_VOICE = 'FunAudioLLM/CosyVoice2-0.5B:anna'

function gatewayEndpoint(path) {
  if (!API_GATEWAY_URL || !/^https?:\/\//i.test(API_GATEWAY_URL)) return ''
  return `${API_GATEWAY_URL}${path}`
}

function sameRock5aEndpoint(left, right) {
  if (!left || !right) return false
  const leftSecure = left.secure === true || left.ws_protocol === 'wss'
  const rightSecure = right.secure === true || right.ws_protocol === 'wss'
  return (left.ip || left.host) === (right.ip || right.host)
    && (left.port || 8770) === (right.port || 8770)
    && (left.audio_port || 8771) === (right.audio_port || 8771)
    && (left.control_path || '') === (right.control_path || '')
    && (left.audio_path || '') === (right.audio_path || '')
    && (left.token || '') === (right.token || '')
    && leftSecure === rightSecure
}

export class VoiceService {
  constructor(options = {}) {
    const injectedSession = options && typeof options.getStatus === 'function'
      ? options
      : options.session
    this._session = injectedSession || getRock5aDeviceSession()
    this._sessionOwner = Symbol('voice-service')
    this._listeningAudioOwner = Symbol('voice-listening')
    this._ttsAudioOwner = Symbol('voice-tts')
    this._sessionUnsubscribers = []
    this._device = null
    this._connectionContext = null
    this._rock5a = this._session // Compatibility for callers that only inspect the field.
    this._audioReceiver = null
    this._xfWebSocket = null
    this._asrAppId = ''
    this._ttsAudioCtx = null
    this._ttsSource = null
    this._ttsAbortController = null
    this._isListening = false
    this._isSpeaking = false
    this._listeningStartPromise = null
    this._listeningStartToken = 0
    this._ttsAudioReleaseTimer = null
    this._pendingTtsRequestId = null
    this._speakingToken = 0
    this._recognizedCb = null
    this._submitCb = null
    this._llmCb = null
    this._commandCb = null
    this._executeCb = null
    this._stoppedCb = null  // 当 STT 自动停止时通知 SmartOrb 同步状态
    // 新增：TTS 音频是否通过 Rock5A/ReSpeaker 播放
    this._useRock5aTTS = true
    // 调试统计
    this._audioChunksReceived = 0
    this._framesSentToSTT = 0
  }

  connect(context, compatibilityDevice = null) {
    const device = compatibilityDevice
      || (context && context.controlWsUrl ? connectionContextToDevice(context) : context)
    if (!device) return { ok: false, reason: 'device_required' }
    if (this._device && !sameRock5aEndpoint(this._device, device)) this.disconnect()
    this._device = device
    this._connectionContext = context && context.controlWsUrl ? context : null
    if (!this._audioReceiver) this._audioReceiver = new Rock5aAudioReceiver()
    this._audioChunksReceived = 0
    this._subscribeToSession()
    this._session.acquire(device, this._sessionOwner, { audio: false })
    // 定期输出音频接收统计
    if (!this._statsTimer) {
      this._statsTimer = setInterval(() => {
        if (this._audioChunksReceived > 0 || this._framesSentToSTT > 0) {
          console.log(`[VoiceService] 音频统计: 收到 ${this._audioChunksReceived} 帧, 发送STT ${this._framesSentToSTT} 帧`)
        }
      }, 5000)
    }
    return { ok: true, status: this._session.getStatus() }
  }

  disconnect() {
    this.stopListening()
    this.stopSpeaking()
    if (this._audioReceiver) { this._audioReceiver.stop(); this._audioReceiver = null }
    this._unsubscribeFromSession()
    this._session.releaseAudio(this._listeningAudioOwner)
    this._session.releaseAudio(this._ttsAudioOwner)
    this._session.release(this._sessionOwner)
    this._device = null
    this._connectionContext = null
    if (this._statsTimer) {
      clearInterval(this._statsTimer)
      this._statsTimer = null
    }
  }

  get isConnected() {
    return this._session.getStatus().control === 'connected'
  }

  get isAudioConnected() { return this._session.getStatus().audio === 'connected' }
  get isAudioFresh() { return this._session.getStatus().freshness.audio.fresh }
  get connectionStatus() { return this._session.getStatus() }

  onRecognized(cb) { this._recognizedCb = cb }
  onSubmit(cb) { this._submitCb = cb }
  onStopped(cb) { this._stoppedCb = cb }

  _subscribeToSession() {
    this._unsubscribeFromSession()
    this._sessionUnsubscribers = [
      this._session.on('audioChunk', (arrayBuffer) => {
        this._audioChunksReceived++
        if (this._audioChunksReceived <= 3) {
          console.log(`[VoiceService] 收到音频帧 #${this._audioChunksReceived}: ${arrayBuffer.byteLength} bytes`)
        }
        if (this._audioReceiver) this._audioReceiver.feed(arrayBuffer)
      }),
      this._session.on('status', (status) => {
        this._device = status.device || null
        if (
          this._isListening
          && (status.audio !== 'connected' || !status.freshness.audio.fresh)
        ) {
          console.warn('[VoiceService] Rock5A 音频链路不可用，停止当前监听')
          this.stopListening()
        }
      }),
      this._session.on('ttsAck', (ack) => {
        if (!ack || ack.request_id !== this._pendingTtsRequestId) return
        if (ack.status === 'error' || ack.status === 'rejected') {
          console.warn('[VoiceService] Rock5A 拒绝 TTS 音频:', ack.reason || ack.status)
          this.stopSpeaking()
        }
      }),
    ]
  }

  _unsubscribeFromSession() {
    for (const unsubscribe of this._sessionUnsubscribers) unsubscribe()
    this._sessionUnsubscribers = []
  }

  async startListening() {
    if (this._isListening) return { ok: true, alreadyListening: true }
    if (this._listeningStartPromise) return await this._listeningStartPromise
    if (!this._device || !this._audioReceiver) {
      console.warn('[VoiceService] Rock5A 未连接，请先连接终端')
      return { ok: false, reason: 'rock5a_not_connected' }
    }
    if (!gatewayEndpoint(ASR_SESSION_PATH) || !API_GATEWAY_TOKEN) {
      console.error('[VoiceService] API gateway URL or access token is not configured')
      return { ok: false, reason: 'gateway_not_configured' }
    }

    const token = ++this._listeningStartToken
    this._listeningStartPromise = this._startListeningInternal(token)
    try {
      return await this._listeningStartPromise
    } finally {
      if (token === this._listeningStartToken) this._listeningStartPromise = null
    }
  }

  async _startListeningInternal(token) {
    if (!this._session.requestAudio(this._listeningAudioOwner)) {
      return { ok: false, reason: 'audio_request_rejected' }
    }

    const audioReady = await this._session.waitForChannel('audio', {
      fresh: true,
      timeoutMs: 5000,
    })
    if (!audioReady.ok || token !== this._listeningStartToken) {
      // stopListening() 已释放旧令牌的 owner；这里再次释放会误伤随后启动的新一轮监听。
      if (token === this._listeningStartToken) {
        this._session.releaseAudio(this._listeningAudioOwner)
      }
      return {
        ok: false,
        reason: token !== this._listeningStartToken ? 'cancelled' : 'audio_unavailable',
        status: audioReady.status,
      }
    }

    this._framesSentToSTT = 0
    this._partialText = ''
    this._audioReceiver.start()
    console.log('[VoiceService] 音频链路就绪，连接讯飞 STT...')

    let socket
    try {
      socket = new WebSocket(await this._getWsUrl())
    } catch (error) {
      this._audioReceiver.stop()
      this._session.releaseAudio(this._listeningAudioOwner)
      return { ok: false, reason: 'stt_socket_create_failed', error }
    }
    this._xfWebSocket = socket

    return await new Promise((resolve) => {
      let settled = false
      const settle = (result) => {
        if (settled) return
        settled = true
        clearTimeout(openTimer)
        resolve(result)
      }
      const openTimer = setTimeout(() => {
        if (token !== this._listeningStartToken) {
          settle({ ok: false, reason: 'cancelled' })
          return
        }
        if (this._xfWebSocket === socket) {
          try { socket.close() } catch (_) {}
          this._xfWebSocket = null
        }
        this._audioReceiver.stop()
        this._session.releaseAudio(this._listeningAudioOwner)
        settle({ ok: false, reason: 'stt_connect_timeout' })
      }, 8000)

      socket.onopen = () => {
        if (token !== this._listeningStartToken || this._xfWebSocket !== socket) {
          try { socket.close() } catch (_) {}
          settle({ ok: false, reason: 'cancelled' })
          return
        }
        this._isListening = true
        console.log('[VoiceService] 讯飞 STT 已连接，开始发送音频帧')
        this._sendFirstFrame()
        this._audioReceiver.onFrameReady((base64Pcm) => {
          if (this._xfWebSocket === socket && socket.readyState === WebSocket.OPEN && this._isListening) {
            this._framesSentToSTT++
            socket.send(JSON.stringify({
              data: { status: 1, format: 'audio/L16;rate=16000', encoding: 'raw', audio: base64Pcm }
            }))
          }
        })
        settle({ ok: true, status: this._session.getStatus() })
      }

      socket.onmessage = (e) => this._handleXFResult(e.data)
      socket.onerror = (e) => {
        console.error('[VoiceService] 讯飞 STT 错误:', e)
        const opened = this._isListening
        if (this._xfWebSocket === socket) this.stopListening()
        if (!opened) settle({ ok: false, reason: 'stt_connect_error' })
      }
      socket.onclose = (e) => {
        console.log(`[VoiceService] 讯飞 STT 连接关闭 (code=${e.code}, reason=${e.reason})`)
        const opened = this._isListening
        if (this._xfWebSocket === socket) this.stopListening()
        if (!opened) settle({ ok: false, reason: 'stt_closed_before_ready' })
      }
    })
  }

  stopListening() {
    this._listeningStartToken++
    this._listeningStartPromise = null
    const wasListening = this._isListening
    this._isListening = false
    if (this._audioReceiver) this._audioReceiver.stop()
    const socket = this._xfWebSocket
    this._xfWebSocket = null
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        data: { status: 2, format: 'audio/L16;rate=16000', audio: '', encoding: 'raw' }
      }))
      socket.onclose = null
      socket.onerror = null
      socket.close()
    } else if (socket) {
      socket.onclose = null
      socket.onerror = null
      try { socket.close() } catch (_) {}
    }
    this._session.releaseAudio(this._listeningAudioOwner)
    // 通知 SmartOrb 同步 isListening 状态（STT 自动关闭时 critical）
    if (wasListening && this._stoppedCb) {
      try { this._stoppedCb() } catch (e) { console.warn('[VoiceService] _stoppedCb error:', e) }
    }
  }

  get isListening() { return this._isListening }

  async _getWsUrl() {
    const response = await fetch(gatewayEndpoint(ASR_SESSION_PATH), {
      method: 'POST',
      headers: { Authorization: `Bearer ${API_GATEWAY_TOKEN}` },
    })
    if (!response.ok) throw new Error(`ASR session ${response.status}`)
    const session = await response.json()
    const signedUrl = new URL(session.url)
    if (
      signedUrl.protocol !== 'wss:'
      || signedUrl.hostname !== 'iat-api.xfyun.cn'
      || signedUrl.pathname !== '/v2/iat'
    ) {
      throw new Error('ASR session returned a forbidden endpoint')
    }
    this._asrAppId = String(session.appId || '')
    if (!this._asrAppId) throw new Error('ASR session missing appId')
    return signedUrl.toString()
  }

  _sendFirstFrame() {
    const p = {
      common: { app_id: this._asrAppId },
      business: { language: 'zh_cn', domain: 'iat', accent: 'mandarin', vad_eos: 3000, dwa: 'wpgs' },
      data: { status: 0, format: 'audio/L16;rate=16000', encoding: 'raw', audio: '' }
    }
    if (this._xfWebSocket && this._xfWebSocket.readyState === WebSocket.OPEN) {
      this._xfWebSocket.send(JSON.stringify(p))
    }
  }

  _handleXFResult(res) {
    let d
    try {
      d = JSON.parse(res)
    } catch (_) {
      console.warn('[VoiceService] 忽略无法解析的讯飞 STT 消息')
      return
    }
    if (d.code !== 0) {
      console.warn('[VoiceService] 讯飞 STT 返回错误:', d.code, d.message || '')
      return
    }
    if (d.data && d.data.result) {
      let s = '', w = d.data.result.ws
      for (let i = 0; i < w.length; i++) s += w[i].cw[0].w
      if (d.data.result.pgs === 'rpl') this._partialText = ''
      this._partialText = (this._partialText || '') + s
      if (this._recognizedCb) this._recognizedCb(this._partialText)
      if (d.data.status === 2) {
        this.stopListening()
        if (this._submitCb && this._partialText) this._submitCb(this._partialText)
        this._partialText = ''
      }
    }
  }

  get isSpeaking() { return this._isSpeaking }

  async speak(text) {
    if (!text) return false
    this.stopSpeaking()
    const token = this._speakingToken
    this._isSpeaking = true
    const abortController = new AbortController()
    this._ttsAbortController = abortController
    console.log(`[VoiceService] speak() 开始 TTS: "${text.substring(0, 50)}..."`)

    try {
      const response = await fetch(gatewayEndpoint(TTS_API_PATH), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${API_GATEWAY_TOKEN}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: TTS_MODEL,
          input: text,
          voice: TTS_VOICE,
          sample_rate: 32000,
        }),
        signal: abortController.signal
      })

      if (token !== this._speakingToken) return false

      if (!response.ok) {
        const errText = await response.text().catch(() => '')
        console.error(`[VoiceService] TTS API ${response.status}:`, errText.substring(0, 200))
        this._isSpeaking = false
        return false
      }

      const arrayBuffer = await response.arrayBuffer()
      if (token !== this._speakingToken) return false

      // 优先按需打开 Rock5A 音频通道，不依赖控制通道伪在线状态。
      if (this._useRock5aTTS && this._device) {
        console.log('[VoiceService] 使用 Rock5A 播放 TTS')
        const sent = await this._sendTTSViaRock5a(arrayBuffer, token)
        if (sent) return true
      }

      if (token !== this._speakingToken) return false
      console.log('[VoiceService] Rock5A 播放不可用，降级到本地播放')
      return await this._playLocally(arrayBuffer, token)
    } catch (err) {
      if (err.name !== 'AbortError' && token === this._speakingToken) {
        this._isSpeaking = false
        console.error('[VoiceService] TTS 失败:', err)
      }
      return false
    }
  }

  stopSpeaking() {
    this._speakingToken++
    if (this._ttsAbortController) { this._ttsAbortController.abort(); this._ttsAbortController = null }
    if (this._ttsSource) { try { this._ttsSource.stop() } catch(e) {} this._ttsSource = null }
    if (this._ttsAudioReleaseTimer) {
      clearTimeout(this._ttsAudioReleaseTimer)
      this._ttsAudioReleaseTimer = null
    }
    this._pendingTtsRequestId = null
    this._session.releaseAudio(this._ttsAudioOwner)
    this._isSpeaking = false
  }

  /**
   * 通过 Rock5A 播放 TTS 音频
   */
  async _sendTTSViaRock5a(arrayBuffer, speakingToken = null) {
    const isCurrent = () => speakingToken === null || speakingToken === this._speakingToken
    if (!isCurrent()) return false
    if (!this._session.requestAudio(this._ttsAudioOwner)) return false

    try {
      const audioReadyPromise = this._session.waitForChannel('audio', {
        fresh: false,
        timeoutMs: 5000,
      })
      // decodeAudioData 会自动重采样到 AudioContext 的 sampleRate
      const AC = window.AudioContext || window.webkitAudioContext
      const ctx = new AC()  // 使用默认采样率，让浏览器自行处理
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
      const audioReady = await audioReadyPromise
      if (!audioReady.ok || !isCurrent()) {
        await ctx.close()
        if (isCurrent()) this._session.releaseAudio(this._ttsAudioOwner)
        return false
      }

      // 提取 PCM 数据（16bit），重采样到 16kHz 发送给 Rock5A
      const targetSr = 16000
      const sourceSr = audioBuffer.sampleRate
      const sourceData = audioBuffer.getChannelData(0)
      // 线性重采样到 16kHz，减少语音在 Rock5A 扬声器上的高频毛刺。
      const ratio = sourceSr / targetSr
      const targetLen = Math.floor(sourceData.length / ratio)
      const pcm = new Int16Array(targetLen)
      for (let i = 0; i < targetLen; i++) {
        const sourcePosition = i * ratio
        const sourceIndex = Math.floor(sourcePosition)
        const nextIndex = Math.min(sourceIndex + 1, sourceData.length - 1)
        const fraction = sourcePosition - sourceIndex
        const interpolated = sourceData[sourceIndex] * (1 - fraction)
          + sourceData[nextIndex] * fraction
        const s = Math.max(-1, Math.min(1, interpolated))
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
      }

      if (!isCurrent()) {
        await ctx.close()
        return false
      }

      // 通过 WS 发送 PCM 数据到 Rock5A
      console.log(`[VoiceService] 发送 TTS 到 Rock5A: ${pcm.length} samples (${(targetLen/targetSr).toFixed(1)}s)`)
      const requestId = `tts-${Date.now()}-${Math.random().toString(16).slice(2)}`
      this._pendingTtsRequestId = requestId
      const metadataSent = this._session.sendAudio(JSON.stringify({
        type: 'tts_meta',
        request_id: requestId,
        sample_rate: targetSr,
        channels: 1,
        sample_format: 's16le',
        byte_length: pcm.byteLength,
      }))
      if (!metadataSent || !this._session.sendAudio(pcm.buffer)) {
        this._pendingTtsRequestId = null
        await ctx.close()
        this._session.releaseAudio(this._ttsAudioOwner)
        return false
      }

      // 等待播放完成
      const duration = (pcm.length / targetSr) * 1000
      this._ttsAudioReleaseTimer = setTimeout(() => {
        if (!isCurrent()) return
        this._isSpeaking = false
        this._ttsAudioReleaseTimer = null
        this._pendingTtsRequestId = null
        this._session.releaseAudio(this._ttsAudioOwner)
      }, duration + 200)

      await ctx.close()
      return true
    } catch (err) {
      if (isCurrent()) {
        this._session.releaseAudio(this._ttsAudioOwner)
        console.error('[VoiceService] Rock5A TTS 发送失败:', err)
      }
      return false
    }
  }

  /**
   * 本地播放 TTS 音频（PC扬声器）
   */
  async _playLocally(arrayBuffer, speakingToken = null) {
    const isCurrent = () => speakingToken === null || speakingToken === this._speakingToken
    if (!isCurrent()) return false
    const AC = window.AudioContext || window.webkitAudioContext
    if (!this._ttsAudioCtx) { this._ttsAudioCtx = new AC({ sampleRate: 16000 }) }
    if (this._ttsAudioCtx.state === 'suspended') {
      try { await this._ttsAudioCtx.resume() } catch(e) {}
    }
    const audioBuffer = await this._ttsAudioCtx.decodeAudioData(arrayBuffer)
    if (!isCurrent()) return false
    this._playAudioBuffer(audioBuffer, speakingToken)
    return true
  }

  _playAudioBuffer(buffer, speakingToken = null) {
    if (!this._ttsAudioCtx) return
    if (this._ttsSource) { try { this._ttsSource.stop() } catch(e) {} }
    const source = this._ttsAudioCtx.createBufferSource()
    source.buffer = buffer
    source.connect(this._ttsAudioCtx.destination)
    source.onended = () => {
      const isCurrent = speakingToken === null || speakingToken === this._speakingToken
      if (this._ttsSource !== source || !isCurrent) return
      this._isSpeaking = false
      this._ttsSource = null
    }
    source.start(0)
    this._ttsSource = source
  }

  onCommand(cb) { this._commandCb = cb }

  execute(text) {
    if (!text) return
    if (this._executeCb) {
      this._executeCb(text)
    }
  }

  onExecute(cb) { this._executeCb = cb }
}
